// Acute-illness episode tracking (ILLNESS_EPISODE_TRACKING_PLAN.md).
// Pure, deterministic — safe to import from both the API (Lambda) and the web SPA.
//
// Two halves, structurally copied from their symptom equivalents:
//   • buildOpenIllnesses()  mirrors symptomFollowUp.ts's buildOpenSymptoms  (follow-up engine)
//   • buildIllnessStats()   mirrors symptomStats.ts's  buildSymptomStats    (report aggregate)
// Both operate ONLY on `episodic === true` illness events (D-IE-1); a chronic
// condition never gets a follow-up card and never appears in the report.
//
// Decisions encoded here:
//   D-IE-4   episode key = normalized name + synonym map, exact lookup (no substring)
//   D-IE-14  duration is WHOLE DAYS from the onset CalendarDate to the resolution
//            CalendarDate, floored at 1 — never mix ISO instants into that math
//   D-IE-15  resolution sentinel: status === "resolved" OR payload.resolvedAt truthy
//            (illness has NO 0-10 severity scale — do NOT copy the symptom engine's
//            severity === 0 check)

import type { MedicalEvent, Patient } from "./types.js";
import type { IllnessStatus, IllnessSeverity } from "./illness.js";
import { resolveProactiveSettings } from "./proactiveSuggestions.js";
import {
  localCalendarDate,
  calendarDaysBetween,
  calendarDateToUtcInstant,
  toCalendarDate,
} from "./datetime.js";

// Mirrors symptomStats.ts's VIEWER_TZ convention. The stats half is only ever
// invoked client-side, so the runtime's resolved timezone is the viewer's local
// timezone; in the Lambda (which only calls buildOpenIllnesses, whose day math is
// pure elapsed time) this resolves to "UTC" and never feeds the duration math.
const VIEWER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

const MS_PER_DAY = 86_400_000;
const MS_PER_WEEK = 7 * MS_PER_DAY;
const MS_PER_YEAR = 365.25 * MS_PER_DAY;

const round1 = (n: number): number => Math.round(n * 10) / 10;

// ── Identity (D-IE-4) ───────────────────────────────────────────────────────

/** Normalize an illness name for identity comparison: trim + lowercase. */
export function normalizeIllnessName(name: unknown): string {
  return String(name ?? "").trim().toLowerCase();
}

/**
 * Conservative synonym map: unambiguous lay variants of the same acute illness
 * → a single canonical name. All keys are pre-normalized (trim + lowercase).
 * Exact-key lookup only — NO substring/`includes` matching, so "cold sore" never
 * folds into "cold". Deliberately excludes clinical near-neighbours ("cough" is
 * NOT bronchitis, "cold" is NOT flu) so grouped output isn't misleading
 * (same rule as symptomClusters.ts's SYMPTOM_SYNONYMS).
 */
export const ILLNESS_SYNONYMS: Record<string, string> = {
  // common cold
  "head cold": "cold",
  "common cold": "cold",
  "the cold": "cold",
  // influenza
  "influenza": "flu",
  "the flu": "flu",
  "flu bug": "flu",
  // gastroenteritis / stomach bug
  "stomach flu": "stomach bug",
  "stomach virus": "stomach bug",
  "gastroenteritis": "stomach bug",
  "gastro": "stomach bug",
  "tummy bug": "stomach bug",
  // covid
  "covid-19": "covid",
  "covid 19": "covid",
  "covid19": "covid",
  "coronavirus": "covid",
  "sars-cov-2": "covid",
  // sinusitis
  "sinus infection": "sinusitis",
  "sinus infections": "sinusitis",
  // strep
  "strep": "strep throat",
  // urinary tract infection
  "uti": "urinary tract infection",
  "u.t.i.": "urinary tract infection",
  "bladder infection": "urinary tract infection",
  // conjunctivitis
  "pink eye": "conjunctivitis",
  "pinkeye": "conjunctivitis",
};

/** D-IE-4: ILLNESS_SYNONYMS[normalize(name)] ?? normalize(name). */
export function illnessEpisodeKey(name: string): string {
  const n = normalizeIllnessName(name);
  return ILLNESS_SYNONYMS[n] ?? n;
}

// ── Constants ───────────────────────────────────────────────────────────────

export const DEFAULT_ILLNESS_FOLLOWUP_LOOKBACK_WEEKS = 4;
export const ILLNESS_FOLLOWUP_LOOKBACK_OPTIONS = [2, 4, 6, 8, 12, 16] as const;
export type IllnessFollowUpLookbackWeeks = (typeof ILLNESS_FOLLOWUP_LOOKBACK_OPTIONS)[number];

export const DEFAULT_ILLNESS_AUTO_CLOSE_DAYS = 21;
export const ILLNESS_AUTO_CLOSE_DAY_OPTIONS = [14, 21, 30, 45, 60] as const;

export const DEFAULT_ILLNESS_SNOOZE_HOURS = 12;
export const ILLNESS_SNOOZE_HOURS_OPTIONS = [4, 8, 12, 24, 48] as const;

export const DEFAULT_ILLNESS_REMINDER_INTERVAL_DAYS = 3;
export const ILLNESS_REMINDER_INTERVAL_DAYS_OPTIONS = [1, 2, 3, 5, 7, 14] as const;
export type IllnessReminderIntervalDays = (typeof ILLNESS_REMINDER_INTERVAL_DAYS_OPTIONS)[number];

/** Clamp a stored per-illness interval to a valid option, else the default (3). */
export function resolveIllnessReminderIntervalDays(
  map: Record<string, number> | undefined,
  key: string
): number {
  const v = map?.[key];
  return (ILLNESS_REMINDER_INTERVAL_DAYS_OPTIONS as readonly number[]).includes(v as number)
    ? (v as number)
    : DEFAULT_ILLNESS_REMINDER_INTERVAL_DAYS;
}

// ── Episode segmentation (shared by follow-up + stats) ──────────────────────

export interface IllnessEpisode {
  key: string;
  /** Display wording from the episode's first event. */
  name: string;
  /** ISO instant — episode's first event occurredAt. */
  startAt: string;
  /** ISO instant — earliest of onsetDate / diagnosedOn / occurredAt in the
   *  episode, resolved to local noon. */
  onsetAt: string;
  /** Bare YYYY-MM-DD of `onsetAt` — the value D-IE-14 duration math uses. */
  onsetDate: string;
  /** ISO instant of the resolving event's occurredAt, if the episode is closed. */
  resolvedAt?: string;
  /** The resolving event's payload.resolvedAt CalendarDate, if it carried one. */
  resolvedOnDate?: string;
  ongoing: boolean;
  /** Chronological, oldest first. */
  events: MedicalEvent[];
}

/** D-IE-15 resolution sentinel: status === "resolved" OR resolvedAt truthy. */
function isIllnessResolution(e: MedicalEvent): boolean {
  const p = e.payload as Record<string, unknown>;
  return p.status === "resolved" || Boolean(p.resolvedAt);
}

function firstString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

function buildEpisode(key: string, events: MedicalEvent[], closed: boolean): IllnessEpisode {
  const first = events[0];
  const firstPayload = first.payload as Record<string, unknown>;
  const name = firstString(firstPayload.name) ?? key;

  // Onset CalendarDate = earliest across the episode of onsetDate / diagnosedOn /
  // the event's local calendar day. CalendarDates sort lexicographically ==
  // chronologically, so a plain string min is correct.
  let onsetDate: string | undefined;
  for (const e of events) {
    const p = e.payload as Record<string, unknown>;
    const candidates = [
      toCalendarDate(p.onsetDate),
      toCalendarDate(p.diagnosedOn),
      localCalendarDate(e.occurredAt, VIEWER_TZ),
    ];
    for (const c of candidates) {
      if (c && (onsetDate === undefined || c < onsetDate)) onsetDate = c;
    }
  }
  onsetDate = onsetDate ?? localCalendarDate(first.occurredAt, VIEWER_TZ);
  const onsetAt = calendarDateToUtcInstant(onsetDate, VIEWER_TZ);

  const episode: IllnessEpisode = {
    key,
    name,
    startAt: first.occurredAt,
    onsetAt,
    onsetDate,
    ongoing: !closed,
    events,
  };
  if (closed) {
    const last = events[events.length - 1];
    episode.resolvedAt = last.occurredAt;
    const rod = toCalendarDate((last.payload as Record<string, unknown>).resolvedAt);
    if (rod) episode.resolvedOnDate = rod;
  }
  return episode;
}

/**
 * Segment `episodic === true`, non-deleted illness events into
 * resolution-bounded episodes (D-IE-15). A new episode begins at the first event
 * of a name-group and immediately after any resolution event.
 */
export function segmentIllnessEpisodes(illnessEvents: MedicalEvent[]): IllnessEpisode[] {
  const groups = new Map<string, MedicalEvent[]>();
  const eligible = illnessEvents
    .filter(
      (e) =>
        !e.deleted &&
        e.eventType === "illness" &&
        (e.payload as Record<string, unknown>).episodic === true
    )
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  for (const e of eligible) {
    const key = illnessEpisodeKey(String((e.payload as Record<string, unknown>).name ?? ""));
    if (!key) continue;
    const g = groups.get(key);
    if (g) g.push(e);
    else groups.set(key, [e]);
  }

  const episodes: IllnessEpisode[] = [];
  for (const [key, evts] of groups) {
    let current: MedicalEvent[] = [];
    for (const e of evts) {
      current.push(e);
      if (isIllnessResolution(e)) {
        episodes.push(buildEpisode(key, current, true));
        current = [];
      }
    }
    if (current.length) episodes.push(buildEpisode(key, current, false));
  }
  return episodes;
}

// ── Follow-up engine (mirror buildOpenSymptoms) ────────────────────────────

export interface OpenIllness {
  key: string;
  name: string;
  status: IllnessStatus;
  severity?: IllnessSeverity;
  contagious?: boolean;
  symptoms?: string[];
  /** ISO instant. */
  onsetAt: string;
  /** Whole days since onsetAt, floored, min 1. */
  dayCount: number;
  /** ISO of the latest event in the episode. */
  lastUpdatedAt: string;
  lastEventId: string;
  /** !snoozed && now − lastUpdatedAt ≥ reminderIntervalDays. */
  dueForCheckIn: boolean;
  reminderIntervalDays: number;
  nextDueAt: string;
  snoozedUntil?: string;
}

export interface StaleIllness {
  key: string;
  name: string;
  lastEventId: string;
  lastUpdatedAt: string;
  /** Fields carried forward into the auto-resolution event. */
  resolvePayloadBase: Record<string, unknown>;
}

export interface OpenIllnessesResult {
  open: OpenIllness[];
  autoClose: StaleIllness[];
}

function isIllnessSeverity(v: unknown): v is IllnessSeverity {
  return v === "mild" || v === "moderate" || v === "severe";
}

function stringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
  return out.length ? out : undefined;
}

/**
 * Build the list of ongoing acute illnesses for a patient at `now` (D-IE-3).
 * Pure — the caller supplies loaded events; the auto-close writes stay in the API.
 * Due-ness is pure elapsed time (no timezone): due when now − lastUpdatedAt ≥ the
 * illness's resolved reminder interval. Snooze gates BOTH due and auto-close, and
 * is checked before either — preserve that order.
 *
 * Returns { open: [], autoClose: [] } when illnessFollowUpEnabled is false.
 * Lookback: an episode's latest event occurredAt must be ≥ now − lookbackWeeks·7d.
 * Sort: dueForCheckIn first, then lastUpdatedAt desc.
 */
export function buildOpenIllnesses(input: {
  illnessEvents: MedicalEvent[];
  patient: Patient;
  now?: Date;
}): OpenIllnessesResult {
  const now = input.now ?? new Date();
  const settings = resolveProactiveSettings(input.patient.proactiveSuggestions);

  if (!settings.illnessFollowUpEnabled) return { open: [], autoClose: [] };

  const lookbackWeeks = (ILLNESS_FOLLOWUP_LOOKBACK_OPTIONS as readonly number[]).includes(
    settings.illnessFollowUpLookbackWeeks as number
  )
    ? (settings.illnessFollowUpLookbackWeeks as number)
    : DEFAULT_ILLNESS_FOLLOWUP_LOOKBACK_WEEKS;
  const sinceMs = now.getTime() - lookbackWeeks * MS_PER_WEEK;

  const autoCloseDays =
    settings.illnessFollowUpAutoCloseDays === null
      ? null
      : typeof settings.illnessFollowUpAutoCloseDays === "number" &&
        settings.illnessFollowUpAutoCloseDays > 0
      ? settings.illnessFollowUpAutoCloseDays
      : DEFAULT_ILLNESS_AUTO_CLOSE_DAYS;
  const autoCloseMs = autoCloseDays !== null ? autoCloseDays * MS_PER_DAY : null;

  const snoozes: Record<string, string> =
    (settings.illnessSnoozes as Record<string, string>) ?? {};

  const open: OpenIllness[] = [];
  const autoClose: StaleIllness[] = [];

  for (const ep of segmentIllnessEpisodes(input.illnessEvents)) {
    if (!ep.ongoing) continue; // a resolved episode is never an open card

    const latest = ep.events[ep.events.length - 1];
    const lastUpdatedMs = Date.parse(latest.occurredAt);
    if (lastUpdatedMs < sinceMs) continue; // outside the lookback window

    const snoozedUntilRaw = snoozes[ep.key];
    const isSnoozed =
      typeof snoozedUntilRaw === "string" && Date.parse(snoozedUntilRaw) > now.getTime();

    // Auto-close (D-IE-10): only when not snoozed.
    if (!isSnoozed && autoCloseMs !== null && now.getTime() - lastUpdatedMs >= autoCloseMs) {
      const lp = latest.payload as Record<string, unknown>;
      const resolvePayloadBase: Record<string, unknown> = {};
      for (const field of [
        "name", "episodic", "onsetDate", "diagnosedOn", "contagious", "symptoms",
        "bodySite", "laterality", "icd10Code", "diagnosingProvider",
      ] as const) {
        if (lp[field] !== undefined) resolvePayloadBase[field] = lp[field];
      }
      autoClose.push({
        key: ep.key,
        name: ep.name,
        lastEventId: latest.eventId,
        lastUpdatedAt: latest.occurredAt,
        resolvePayloadBase,
      });
      continue;
    }

    const reminderIntervalDays = resolveIllnessReminderIntervalDays(
      settings.illnessReminderIntervalDays as Record<string, number> | undefined,
      ep.key
    );
    const intervalMs = reminderIntervalDays * MS_PER_DAY;
    const dueForCheckIn = !isSnoozed && now.getTime() - lastUpdatedMs >= intervalMs;
    const nextDueAt = new Date(lastUpdatedMs + intervalMs).toISOString();
    const dayCount = Math.max(1, Math.floor((now.getTime() - Date.parse(ep.onsetAt)) / MS_PER_DAY));

    const lp = latest.payload as Record<string, unknown>;
    open.push({
      key: ep.key,
      name: ep.name,
      status: (typeof lp.status === "string" ? lp.status : "active") as IllnessStatus,
      ...(isIllnessSeverity(lp.severity) ? { severity: lp.severity } : {}),
      ...(typeof lp.contagious === "boolean" ? { contagious: lp.contagious } : {}),
      ...(stringArray(lp.symptoms) ? { symptoms: stringArray(lp.symptoms) } : {}),
      onsetAt: ep.onsetAt,
      dayCount,
      lastUpdatedAt: latest.occurredAt,
      lastEventId: latest.eventId,
      dueForCheckIn,
      reminderIntervalDays,
      nextDueAt,
      ...(isSnoozed ? { snoozedUntil: snoozedUntilRaw } : {}),
    });
  }

  open.sort((a, b) => {
    if (a.dueForCheckIn !== b.dueForCheckIn) return a.dueForCheckIn ? -1 : 1;
    return Date.parse(b.lastUpdatedAt) - Date.parse(a.lastUpdatedAt);
  });

  return { open, autoClose };
}

// ── Stats aggregate (mirror buildSymptomStats) ────────────────────────────

export const ILLNESS_STATS_TOP_SYMPTOMS_N = 5;

export interface IllnessStatRow {
  key: string;
  name: string;
  episodeCount: number;
  ongoingCount: number;
  episodesPerYear: number;
  durationDaysMin?: number;
  durationDaysMax?: number;
  durationDaysAvg?: number;
  durationDaysTotal?: number;
  medianGapDays?: number;
  totalDaysMissed: number;
  contagiousCount: number;
  /** length 12, indexed by episode-start local month (0 = Jan). */
  monthHistogram: number[];
  topSymptoms: { value: string; count: number }[];
  firstAt: string;
  lastAt: string;
}

function tallyValue(tally: Map<string, { value: string; count: number }>, raw: unknown): void {
  if (typeof raw !== "string") return;
  const trimmed = raw.trim();
  if (!trimmed) return;
  const dedupeKey = trimmed.toLowerCase();
  const existing = tally.get(dedupeKey);
  if (existing) existing.count += 1;
  else tally.set(dedupeKey, { value: trimmed, count: 1 });
}

function median(nums: number[]): number {
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Per-illness statistics for `episodic === true` illness events, aggregated over
 * episodes whose `startAt` falls in [startMs, endMs]. Duration is the whole-day
 * CalendarDate span from onset to resolution, floored at 1 (D-IE-14). Sort:
 * episodeCount desc, then lastAt desc.
 */
export function buildIllnessStats(
  events: MedicalEvent[],
  startMs: number,
  endMs: number
): IllnessStatRow[] {
  const rangeYears = Math.max((endMs - startMs) / MS_PER_YEAR, 1 / 365.25);

  const byKey = new Map<string, IllnessEpisode[]>();
  for (const ep of segmentIllnessEpisodes(events)) {
    const startAtMs = Date.parse(ep.startAt);
    if (startAtMs < startMs || startAtMs > endMs) continue;
    const g = byKey.get(ep.key);
    if (g) g.push(ep);
    else byKey.set(ep.key, [ep]);
  }

  const rows: IllnessStatRow[] = [];

  for (const [key, episodes] of byKey) {
    episodes.sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt));

    const latestEpisode = episodes[episodes.length - 1];
    const latestEvent = latestEpisode.events[latestEpisode.events.length - 1];
    const name =
      firstString((latestEvent.payload as Record<string, unknown>).name) ?? latestEpisode.name;

    const episodeCount = episodes.length;
    const ongoingCount = episodes.filter((e) => e.ongoing).length;
    const episodesPerYear = round1(episodeCount / rangeYears);

    // Duration (closed episodes only), pure CalendarDate day math (D-IE-14).
    const durations: number[] = [];
    for (const ep of episodes) {
      if (ep.ongoing) continue;
      const resolutionDate =
        ep.resolvedOnDate ??
        (ep.resolvedAt ? localCalendarDate(ep.resolvedAt, VIEWER_TZ) : undefined);
      if (!resolutionDate) continue;
      const d = calendarDaysBetween(ep.onsetDate, resolutionDate);
      if (Number.isFinite(d)) durations.push(Math.max(1, d));
    }
    const hasDurations = durations.length > 0;
    const durationDaysMin = hasDurations ? Math.min(...durations) : undefined;
    const durationDaysMax = hasDurations ? Math.max(...durations) : undefined;
    const durationDaysTotal = hasDurations ? durations.reduce((a, b) => a + b, 0) : undefined;
    const durationDaysAvg = hasDurations
      ? round1(durations.reduce((a, b) => a + b, 0) / durations.length)
      : undefined;

    // Median gap between consecutive episode starts.
    let medianGapDays: number | undefined;
    if (episodes.length >= 2) {
      const gaps: number[] = [];
      for (let i = 1; i < episodes.length; i++) {
        gaps.push(
          Math.round(
            (Date.parse(episodes[i].startAt) - Date.parse(episodes[i - 1].startAt)) / MS_PER_DAY
          )
        );
      }
      medianGapDays = Math.round(median(gaps));
    }

    // Days missed: per-episode MAX (not sum), so a "still sick, missed 2 more
    // days" follow-up restating a running total does not double-count.
    let totalDaysMissed = 0;
    let contagiousCount = 0;
    const monthHistogram = new Array<number>(12).fill(0);
    const symptomTally = new Map<string, { value: string; count: number }>();

    for (const ep of episodes) {
      let epMissed = 0;
      let epContagious = false;
      for (const e of ep.events) {
        const p = e.payload as Record<string, unknown>;
        if (typeof p.missedDays === "number" && Number.isFinite(p.missedDays)) {
          epMissed = Math.max(epMissed, p.missedDays);
        }
        if (p.contagious === true) epContagious = true;
        if (Array.isArray(p.symptoms)) {
          for (const s of p.symptoms) tallyValue(symptomTally, s);
        }
      }
      totalDaysMissed += epMissed;
      if (epContagious) contagiousCount += 1;
      const monthIdx = Number(localCalendarDate(ep.startAt, VIEWER_TZ).slice(5, 7)) - 1;
      if (monthIdx >= 0 && monthIdx < 12) monthHistogram[monthIdx] += 1;
    }

    const topSymptoms = [...symptomTally.values()]
      .sort((a, b) => b.count - a.count)
      .slice(0, ILLNESS_STATS_TOP_SYMPTOMS_N)
      .map((e) => ({ value: e.value, count: e.count }));

    const firstAt = episodes[0].startAt;
    const lastAt = episodes.reduce((acc, ep) => {
      const last = ep.events[ep.events.length - 1].occurredAt;
      return Date.parse(last) > Date.parse(acc) ? last : acc;
    }, episodes[0].events[episodes[0].events.length - 1].occurredAt);

    rows.push({
      key,
      name,
      episodeCount,
      ongoingCount,
      episodesPerYear,
      durationDaysMin,
      durationDaysMax,
      durationDaysAvg,
      durationDaysTotal,
      medianGapDays,
      totalDaysMissed: Math.round(totalDaysMissed),
      contagiousCount,
      monthHistogram,
      topSymptoms,
      firstAt,
      lastAt,
    });
  }

  rows.sort((a, b) => {
    if (b.episodeCount !== a.episodeCount) return b.episodeCount - a.episodeCount;
    return Date.parse(b.lastAt) - Date.parse(a.lastAt);
  });

  return rows;
}
